<template>
  <div class="subject-dist text-dark" :class="{ 'subject-dist--print-table': isPrintingTable }">
    <Banner v-if="noActiveSchoolYears"></Banner>
    <SubjectDistributionPrint
      v-if="isPrintUsers"
      @close="closeIsPrintUsers">
    </SubjectDistributionPrint>
    <template v-else="isPrintUsers">
      <div class="subject-dist__header">
        <div class="subject-dist__header__left">
          <h1>{{ i18n[locale].subjects_for_teachers }}</h1>
          <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
        </div>
        <div class="subject-dist__header__right">
          <template v-if="userCanManageSchool">
            <DDropdown
              :isShowDots="false"
              :isBottomStart="false"
              :propOffset="[0, 8]"
              class="marg-05-right"
            >
              <template #title>
                <button type="button" class="do-bttn do-bttn--menu-button bttn-export">
                  {{ i18n[locale].automatic_scheduling }} <span class="icon-menu-open"></span>
                </button>
              </template>
              <template #list>
                <ul class="do-bttn--menu-button__list">
                  <li @click="isZenExportModal = true">
                    {{ i18n[locale].transfer_subjects_to_zen }}
                  </li>
                  <li v-if="theZENToken.length > 0">
                    <a class="zen-auto-schedule-menu__link" :href="zenUrl" target="_blank">{{ i18n[locale].go_to_zen }}</a>
                  </li>
                </ul>
              </template>
            </DDropdown>
          </template>
          <a v-else-if="theZENToken.length > 0"
             class="do-bttn"
             :href="zenUrl"
             target="_blank">{{ i18n[locale].go_to_zen }}</a>
          <Tabs
            :tabs="viewTabs"
            :selected="selectedViewTabId"
            @select="onSelectView"
          />
        </div>
      </div>
      <Loader v-if="isLoading" color="blue"></Loader>
      <div class="subject-dist__body" v-else>
        <div class="doTable__users">
          <div class="doTable__users__header">
            <DoFiltersFilter
              :tags="groupTags"
              :selectedTags="selectedGroupTags"
              @select="toggleGroupTag"
              @clear="clearSelectedGroupTags"
            ></DoFiltersFilter>
          </div>
          <div class="filter pointer" v-if="!isPoolsDeactivated">
            <input type="checkbox" v-model="isShowOnlyTeacherWithTeachingTime"
                   id="only-teachers-with-teaching_time">
            <label class="marg-5-left pointer no-select" for="only-teachers-with-teaching_time">
              {{ i18n[locale].only_show_users_with_uv_time }}
            </label>
          </div>
          <ul class="d-box users-list">
            <li class="user-item user-item__header">
              <div class="user-sorter" :class="{'user-sorter--active': sortBy === 'name'}"
                   @click="setSortBy('name')">{{ i18n[locale].name }}
                <span class="icon-menu-open"></span></div>
              <div class="user-sorter" :class="{'user-sorter--active': sortBy === 'teachingTimeDiff'}"
                   @click="setSortBy('teachingTimeDiff')">{{ i18n[locale].teaching_hours }} <span
                class="icon-menu-open"></span></div>
            </li>
            <h4 class="explainer-text" v-if="sortedUsersFilteredWithTags.length < 1">
              {{ i18n[locale].no_users_matches }}
            </h4>
            <li class="user-item"
                v-for="user in sortedUsersFilteredWithTags"
                @click="setActiveUserId(user.id)"
                :key="user.user_calendar_id"
                :class="{'user-item--is-open': user.id === activeUserId}">
              <div class="user-item__name" v-if="user.id !== activeUserId">
                <div class="user-item__name__bttn icon__bg-circle hint--right"
                     @click.stop="setTheUserToShowTasks(user)"
                     :aria-label="$t('vue2.task_overview.task_and_hour_overview')">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="icon-chart tw-icon tw-icon--small">
                    <path class="primary"
                          d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2zm11 4a1 1 0 0 0-1 1v8a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1z"/>
                    <path class="secondary"
                          d="M8 11a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0v-4a1 1 0 0 1 1-1zm4-2a1 1 0 0 1 1 1v6a1 1 0 0 1-2 0v-6a1 1 0 0 1 1-1z"/>
                  </svg>
                </div>
                <p>{{ user.name }}</p>
              </div>
              <div v-if="user.id !== activeUserId">
                <span class="icon-file-text2" v-if="userHasAgreements(user.id)"></span>
                {{ isLessonsPerWeekActive ? userLessonsPerWeekMap[user.id] : userTeachingDiffMap[user.id] }}
                <span class="icon-arrow-down4"></span>
              </div>
              <SubjectsDistributionUser
                v-if="user.id === activeUserId"
                :activeUserId="activeUserId"
                :isPoolsDeactivated="isPoolsDeactivated"
                :showLessonsPerWeek="isLessonsPerWeekActive"
                @editSubject="setEditSubject"
                @editTask="setEditTask"
                @showUserTasks="setTheUserToShowTasks"
                @close="resetActiveUserId">
              </SubjectsDistributionUser>
            </li>
          </ul>
        </div>
        <div class="subjects-and-tasks">
          <SubjectsDistributionTasksFromSubjects v-if="isTasksNewView"></SubjectsDistributionTasksFromSubjects>
          <SubjectsDistributionTasks v-else-if="isTasksView"></SubjectsDistributionTasks>
          <SchoolCamps v-else-if="isCampsView"></SchoolCamps>
          <template v-else>
            <div class="subject-dist__header">
              <div class="subject-dist__header__left">
                <DoFiltersPopover
                  :selectedCount="allSelectedFiltersCount"
                  @clearAll="clearAllFilters"
                >
                  <div class="do-filters-popover__item">
                    <h5>{{ i18n[locale].filter_by_status }}</h5>
                    <button class="do-filters-popover__quick-filter"
                            :class="{'do-filters-popover__quick-filter--selected': selectedSubjectStatusFilters.includes(statusFilter)}"
                            v-for="statusFilter in subjectStatusFilters"
                            :key="statusFilter"
                            @click="toggleSelectedSubjectStatusFilters(statusFilter)">
                      <span class="icon-checkmark-circle"></span>
                      {{ statusFilter }}
                    </button>
                  </div>
                  <div class="do-filters-popover__item">
                    <h5>{{ i18n[locale].filter_by_grades }}</h5>
                    <DoFiltersFilter
                      :title="i18n[locale]['grades']"
                      :tags="klassTags"
                      :selectedTags="selectedKlassFilters"
                      @select="toggleKlassTag"
                      @clear="clearSelectedKlassFilters"
                    >
                    </DoFiltersFilter>
                  </div>
                  <div class="do-filters-popover__item">
                    <h5>{{ i18n[locale].filter_by_subjects }}</h5>
                    <DoFiltersFilter
                      :title="i18n[locale]['subjects']"
                      :tags="uniqueSubjects"
                      :selectedTags="selectedSubjectFilters"
                      @select="toggleSelectedSubjectFilters"
                      @clear="clearSelectedSubjectFilters"
                    >
                    </DoFiltersFilter>
                  </div>
                  <div class="do-filters-popover__item">
                    <h5>{{ i18n[locale].filter_by_type }}</h5>
                    <DoFiltersFilter
                      title="Type"
                      :tags="uniqueSubjectTypeLabels"
                      :selectedTags="selectedTypeFilters"
                      @select="toggleSelectedTypeFilters"
                      @clear="clearSelectedTypeFilters"
                    >
                    </DoFiltersFilter>
                  </div>
                  <div class="do-filters-popover__item">
                    <h5>{{ i18n[locale].filter_by_persons }}</h5>
                    <DoFiltersFilter
                      :title="i18n[locale]['persons']"
                      :tags="uniquePersons"
                      :selectedTags="selectedPersonFilters"
                      @select="toggleSelectedPersonFilters"
                      @clear="clearSelectedPersonFilters"
                    >
                    </DoFiltersFilter>
                  </div>
                </DoFiltersPopover>
              </div>
              <div class="subject-dist__header__right">
                <div class="do-spinner do-spinner--small" v-if="isUpdatingSchoolYear"></div>
                <DDropdown
                  v-if="isSchoolDanish && isTableView"
                  :isShowDots="false"
                  :isBottomStart="false"
                  :propOffset="[0, 8]"
                  class="marg-05-right"
                >
                  <template #title>
                    <button class="do-bttn do-bttn--menu-button">
                      <span v-if="isDownloadingExcel" class="do-spinner do-spinner--small"></span>
                      <span v-else class="icon-printer3"></span>
                      <span class="icon-menu-open"></span>
                    </button>
                  </template>
                  <template #list>
                    <ul class="do-bttn--menu-button__list do-bttn--menu-button__list__print">
                      <li @click="printTable">
                        <span class="icon-printer3"></span> Print tabel 
                      </li>
                      <li @click="asyncLoadXLSXAndDownloadTable">
                        <span class="icon-download"></span> Download til excel
                      </li>
                    </ul>
                  </template>
                </DDropdown>
                <DDropdown v-if="isSchoolDanish" :isShowDots="false" :isBottomStart="false" :isCloseOnDropdownClick="false" :propOffset="[0, 8]" class="marg-05-right">
                  <template #title>
                    <button class="do-bttn do-bttn--menu-button">Visning <span class="icon-menu-open"></span></button>
                  </template>
                  <template #list>
                    <ul class="do-bttn--menu-button__list">
                      <li @click="viewMode = viewMode === 'table' ? 'tiles' : 'table'">
                        Vis som tabel
                        <span v-if="viewMode === 'table'" class="icon-checkmark-circle"></span>
                      </li>
                      <li v-if="isShowSubjectsRequest"
                          @click="showSubjectsRequestInTable = !showSubjectsRequestInTable">
                        Vis fagønsker i tabellen
                        <span v-if="showSubjectsRequestInTable" class="icon-checkmark-circle"></span>
                      </li>
                      <li v-if="isTableView"
                          @click="showDatesInTable = !showDatesInTable">
                        Vis datoer i tabellen
                        <span v-if="showDatesInTable" class="icon-checkmark-circle"></span>
                      </li>
                      <li @click="showLessonsPerWeek = !showLessonsPerWeek">
                        Vis i lektioner
                        <span v-if="showLessonsPerWeek" class="icon-checkmark-circle"></span>
                      </li>
                      <li @click="showActivities = !showActivities">
                        Vis aktiviteter i klasserne
                        <span v-if="showActivities" class="icon-checkmark-circle"></span>
                      </li>
                      <li @click="toggleDoesRessCount"
                          content="Slå til hvis ressourcetimer ikke skal medregnes i klassens totale timetal"
                          v-tippy="{ arrow: true, placement: 'left' }">
                        Ikke medregn ressourcetimer
                        <span v-if="!doesRessCountInKlassTotalHours" class="icon-checkmark-circle"></span>
                      </li>
                    </ul>
                  </template>
                </DDropdown>
                <DoButtonAndDate
                  content="Fag kan ønskes af brugere med rettigheden"
                  v-tippy="{
                    arrow: false,
                    placement : 'top'
                  }"
                  v-if="userCanManageSchool && isSchoolDanish"
                  :selectedDate="subjectsRequestEndDate"
                  @dateChanged="onSetSubjectsRequestEndDate">
                </DoButtonAndDate>
              </div>
            </div>
            <div v-if="isTableView && isEditingDates" class="bulk-dates-bar">
              <button class="do-bttn" @click="toggleSelectAllVisible">
                {{ allVisibleSelected ? 'Fravælg alle' : 'Vælg alle' }}
              </button>
              <span class="bulk-dates-bar__count">{{ selectedSubjectIds.length }} valgt</span>
              <template v-if="selectedSubjectIds.length > 0">
                <DatePickerRange
                  :startdate="bulkFromDate"
                  :enddate="bulkToDate"
                  :minDate="bulkDateMin"
                  :maxDate="bulkDateMax"
                  :maxEndDate="bulkDateMax"
                  :allowSameDate="true"
                  @startchanged="setBulkFromDate"
                  @endchanged="setBulkToDate">
                </DatePickerRange>
                <button class="do-bttn do-bttn--submit"
                        :disabled="isSavingBulkDates"
                        :class="{'do-bttn--submit--loading': isSavingBulkDates}"
                        @click="saveBulkDates">
                  Gem
                </button>
              </template>
              <button class="do-bttn bulk-dates-bar__cancel" @click="cancelEditDates">Annuller</button>
            </div>
            <SubjectsDistributionTable
              v-if="isTableView"
              :doesRessCountInKlassTotalHours="doesRessCountInKlassTotalHours"
              :subjectDefaultDuration="subjectDefaultDuration"
              :filteredKlassCals="filteredKlassCals"
              :showLessonsPerWeek="showLessonsPerWeek"
              :showTeacherPoolHours="showTeacherPoolHours"
              :showSubjectsRequest="showSubjectsRequestInTable && isShowSubjectsRequest && Object.keys(subjectsRequestsInHash).length > 0"
              :showDates="showDatesInTable"
              :isEditingDates="isEditingDates"
              :selectedSubjectIds="selectedSubjectIds"
              @editSubject="setEditSubject"
              @toggleEditDates="toggleEditDates"
              @toggleSubjectSelection="toggleSubjectSelection">
            </SubjectsDistributionTable>
            <div class="doTable" v-else>
              <h4 class="explainer-text" v-if="filteredKlassCals.length < 1">
                {{ i18n[locale].no_klasses_matches }}
              </h4>
              <ul class=" doTable__items
              ">
              <li class="doTable__klass"
                  v-for="(klassCal, idx) in filteredKlassCals"
                  :key="klassCal.id">
                <div class="doTable__klass__header">
                  <div>{{ klassCal.name }}</div>
                  <div class="doTable__klass__header__flex">
                    <label>{{ i18n[locale].hours_in_school_year }}:</label>
                    <span class="doTable__klass__header__hours"
                          :content="'inkl. reduktioner'"
                          v-tippy="klassCal.vHasReductions ? { arrow: true, animateFill: false, animation: 'shift-away', zIndex: '999999992', boundary: 'window' } : { trigger: 'manual' }">{{ klassCal.vTotalHours }}</span>
                  </div>
                </div>
                <ul class="subjects">
                  <li class="subjects_subject"
                      v-for="subject in klassCal.vSubjects"
                      :key="subject.id"
                      @click="setEditSubject(subject, klassCal.id)"
                      :class="[{'subject--has-user': subject.vHasTeachers}, {'subject--has-room': subject.vHasRooms}, {'subject--has-request': subject.vRequestedBy.length > 0}, {'subject--has-other-klass': subject.v_subjectOtherKlasses && subject.v_subjectOtherKlasses.length > 0}]"
                      :style="{'background': subject.color}"
                      :content="subject.vTooltip"
                      v-tippy="{
                    arrow: true,
                    onShow: () => !!subject.vTooltip
                  }">
                    {{ subject.name }} ({{ showLessonsPerWeek ? subject.vLessonsPerWeek : subject.vHours }}) <span v-if="subject.vIsPartiallyDistributed" class="do-pill do-pill--yellow subject-remaining">{{ showLessonsPerWeek ? subject.vRemainingLessonsPerWeek : subject.vRemainingHours.toFixed(1) }} mangler</span>
                  </li>
                </ul>
              </li>
            </ul>
            </div>
          </template>
        </div>
      </div>
    </template>
    <SubjectForm
      v-if="isSubjectModal"
      :showModal="isSubjectModal"
      :editSubject="editSubject"
      :poolCalendarId="editSubjectPoolCalendarId"
      :propIsLoading="isSubjectLoading"
      :allowDanishPreparationFactorUi="true"
      :isShowSubjectsRequest="isShowSubjectsRequest"
      :subjectsRequestsInHash="subjectsRequestsInHash"
      :showActivities="showActivities"
      @deleteSubject="deleteSubject"
      @close="closeModal"
      @updateSubject="updateSubject"
      @saveAndShowTab="saveAndShowTab">
    </SubjectForm>
    <transition name="d-modal">
      <ModalConfirmation
        v-if="isConfirmDeletion"
        :showClose="false"
        @close="$store.commit('setConfirmDeletion', false)"
        @confirm="confirmDelete">
        <h3 slot="item">{{ itemToDelete.name }}</h3>
      </ModalConfirmation>
    </transition>
    <transition name="d-modal">
      <DModal v-if="theEditTask" :showClose="false">
        <SubjectsDistributionTasksCreate
          slot="insertComponent"
          :editTask="theEditTask"
          @close="theEditTask = null">
        </SubjectsDistributionTasksCreate>
      </DModal>
    </transition>
    <DModal v-if="theUserToShowTasks" @close="closeTheUserToShowTasks" :isBgOverlay="true" :show-close="false" :isPrintable="true">
      <div class="user-task__container" slot="insertComponent">
        <div class="user-task__container__header no-print">
          <h2></h2>
          <div class="icon__bg-circle ww-modal__header__close" @click="closeTheUserToShowTasks">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <UserOverviewTabs
          v-if="theUserToShowTasks"
          :user="theUserToShowTasks"
          initialView="tasks"
          :initialIsShowScheduled="false">
        </UserOverviewTabs>
      </div>
    </DModal>
    <ZenExport
      v-if="isZenExportModal"
      :schoolId="schoolId"
      :schoolYearId="activeSchoolYear.id"
      @close="isZenExportModal = false"
      @exported="onZenExported">
    </ZenExport>
  </div>
</template>

<script>
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import SubjectForm from '../Subjects/SubjectForm'
import TaggingFilter from '../Tagging/TaggingFilter'
import Spinner from '../common/Spinner'
import SubjectsDistributionTasks from './SubjectsDistributionTasks'
import SubjectsDistributionTasksFromSubjects from './SubjectsDistributionTasksFromSubjects.vue'
import SubjectsDistributionUser from './SubjectsDistributionUser'
import ModalConfirmation from '../Modals/ModalConfirmation'
import docendo from '../../docendo'
import SubjectDistributionPrint from './SubjectsDistributionPrint'
import SubjectsDistributionTasksCreate from './SubjectsDistributionTasksCreate'
import DModal from '../common/DModal'
import AppToggle from '../common/AppToggle'
import Loader from '../common/Loader.vue'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import DoFiltersPopover from '../DoFilters/DoFiltersPopover.vue'
import DateSelector from '../datepicker/DateSelector.vue'
import moment from 'moment'
import DoButtonAndDate from '../common/DoButtonAndDate.vue'
import Tabs from '../common/Tabs.vue'
import Banner from '../common/Banner.vue'
import SubjectsDistributionTable from './SubjectsDistributionTable.vue'
import SchoolCamps from './SchoolCamps.vue'
import DDropdown from '../common/DDropdown.vue'
import ZenExport from '../ZenImport/ZenExport.vue'
import DatePickerRange from '../datepicker/DatePickerRange.vue'
import UserOverviewTabs from '../User/UserOverviewTabs'
import eventbus from '../../eventbus'

const I18N = {
  da: {
    name: 'Navn',
    subjects_for_teachers: 'Fagfordeling',
    filter_users: 'Grupper',
    teaching_hours_from_subjects: 'UV-TID FRA FAG',
    created_subject: 'Oprettede faget',
    klass: 'Klasse',
    subjects: 'Fag',
    teaching_hours: 'UV tid',
    no_subjects_assigned: 'Ingen fag tildelt',
    assigned: 'Tildelt',
    from_subjects: 'Fra fag',
    difference: 'Diff',
    only_subjects_without_teachers: 'Vis fag uden underviser',
    only_subjects_with_teachers: 'Vis fag med underviser',
    only_subjects_with_teacher_pool: 'Vis fag med lærerpuljer',
    hours_in_school_year: 'Timer i skoleåret',
    export_to_zen: 'Overfør til automatisk skemalægning',
    go_to_zen: 'Gå til automatisk skemalægning',
    automatic_scheduling: 'Automatisk skemalægning',
    transfer_subjects_to_zen: 'Overfør brikker til automatisk skemalægning',
    exported_to_zen: 'Data overført til Docendo ZEN',
    something_went_wrong: 'Noget gik galt',
    choose_grades: 'Vælg klasser',
    grades: 'Klasser',
    only_show_users_with_uv_time: 'Vis kun brugere med tildelt UV tid',
    tasks: 'Opgaver',
    no_users_matches: 'Ingen brugere matcher dine filtre',
    print: 'Print opgaveoversigter',
    only_subjects_with_requests: 'Vis kun fag med ønsker',
    subjects_status: 'Status',
    camps: 'Lejrskole',
    persons: 'Personer',
    no_klasses_matches: 'Ingen klasser matcher dine filtre',
    filter_by_status: 'Vis brikker der opfylder ét eller flere af følgende kriterier',
    filter_by_grades: 'Vis kun disse klasser',
    filter_by_subjects: 'Vis kun disse fag',
    filter_by_type: 'Vis kun brikker af disse typer',
    filter_by_persons: 'Vis kun brikker hvor disse personer står på',
    tooltip_teachers: 'Undervisere',
    tooltip_rooms: 'Lokaler',
    tooltip_klasses: 'Klasser'
  },
  en: {
    subjects_status: 'Status',
    name: 'name',
    subjects_for_teachers: 'Teacher-subject allocation',
    filter_users: ' Filter users by groups',
    teaching_hours_from_subjects: 'Annual instruction time from subjects',
    created_subject: 'Created subject',
    klass: 'Class',
    subjects: 'Subjects',
    teaching_hours: 'Instruction time',
    no_subjects_assigned: 'No subjects allocated',
    assigned: 'Allocated',
    from_subjects: 'From subjects',
    difference: 'Diff',
    only_subjects_without_teachers: 'Show only subjects without teachers',
    only_subjects_with_teachers: 'Show subjects without teachers',
    only_subjects_with_teacher_pool: 'Show subjects with teacher pools',
    hours_in_school_year: 'Annual Instruction Time',
    export_to_zen: 'Export to automatic scheduling',
    go_to_zen: 'Go to automatic scheduling',
    automatic_scheduling: 'Automatic scheduling',
    transfer_subjects_to_zen: 'Transfer subjects to automatic scheduling',
    exported_to_zen: 'Data is ready in Docendo ZEN',
    something_went_wrong: 'Something went wrong',
    choose_grades: 'Choose grades/groups',
    grades: 'Grades/groups',
    only_show_users_with_uv_time: 'Only show user with assigned teaching hours',
    tasks: 'Tasks',
    no_users_matches: 'No users matches',
    print: 'Print',
    only_subjects_with_requests: 'Show only subjects with requests',
    camps: 'School camps',
    persons: 'People',
    no_klasses_matches: 'No grades/groups match your filters',
    filter_by_status: 'Show subjects that meet one or more of the following criteria',
    filter_by_grades: 'Only show these grades/groups',
    filter_by_subjects: 'Only show these subjects',
    filter_by_type: 'Only show subjects of these types',
    filter_by_persons: 'Only show subjects assigned to these people',
    tooltip_teachers: 'Teachers',
    tooltip_rooms: 'Rooms',
    tooltip_klasses: 'Grades/groups'
  },
  nb: {
    subjects_status: 'Status',
    name: 'Navn',
    subjects_for_teachers: 'Fag- og lærerfordeling',
    filter_users: ' Filtrer etter gruppe',
    teaching_hours_from_subjects: 'Årstimetall',
    created_subject: 'Opprettet faget',
    klass: 'Klasse',
    subjects: 'Fag',
    teaching_hours: 'Timetall',
    no_subjects_assigned: 'Ingen fag tildelt',
    assigned: 'Tildelt',
    from_subjects: 'Fra fag',
    difference: 'Diff',
    only_subjects_without_teachers: 'Vis kun fag uten underviser',
    only_subjects_with_teachers: 'Vis fag uten underviser',
    only_subjects_with_teacher_pool: 'Vis fag med lærerpuljer',
    hours_in_school_year: 'Timer i skoleåret',
    export_to_zen: 'Overfør for automatisk timeplanlegging',
    go_to_zen: 'Gå til automatisk timeplanlegging',
    automatic_scheduling: 'Automatisk timeplanlegging',
    transfer_subjects_to_zen: 'Overfør brikker til automatisk timeplanlegging',
    exported_to_zen: 'Data is ready in Docendo ZEN',
    something_went_wrong: 'Something went wrong',
    choose_grades: 'Velg klasser',
    grades: 'Klasser',
    only_show_users_with_uv_time: 'Vis kun brukere med tildelt undervisning',
    tasks: 'Oppgaver',
    no_users_matches: 'Ingen brukere passer til søket',
    print: 'Skriv ut',
    only_subjects_with_requests: 'Vis kun fag med ønsker',
    camps: 'Leirskole',
    persons: 'Personer',
    no_klasses_matches: 'Ingen klasser passer til filtrene',
    filter_by_status: 'Vis brikker som oppfyller ett eller flere av følgende kriterier',
    filter_by_grades: 'Vis kun disse klassene',
    filter_by_subjects: 'Vis kun disse fagene',
    filter_by_type: 'Vis kun brikker av disse typene',
    filter_by_persons: 'Vis kun brikker hvor disse personene står på',
    tooltip_teachers: 'Lærere',
    tooltip_rooms: 'Lokaler',
    tooltip_klasses: 'Klasser'
  }
}

export default {
  components: {
    Tabs,
    DoButtonAndDate,
    DateSelector,
    DoFiltersFilter,
    DoFiltersPopover,
    Loader,
    AppToggle,
    ModalConfirmation,
    ChangeSchoolYear,
    SubjectForm,
    TaggingFilter,
    Spinner,
    SubjectsDistributionTasks,
    SubjectsDistributionTasksFromSubjects,
    SubjectsDistributionUser,
    SubjectDistributionPrint,
    SubjectsDistributionTasksCreate,
    DModal,
    Banner,
    SubjectsDistributionTable,
    SchoolCamps,
    DDropdown,
    ZenExport,
    DatePickerRange,
    UserOverviewTabs
  },
  watch: {
    activeSchoolYear (val) {
      this.readAssignedHours()
      this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
      this.setSubjectsRequestEndDate(this.activeSchoolYear)
      this.readSubjectRequests()
    },
    onlyShowSubjectsWithoutTeacherI18n () {
      if (!this.isShowSubjectsWithoutTeachers) {
        this.selectedSubjectStatusFilters.push(this.onlyShowSubjectsWithoutTeacherI18n)
      }
    },
    viewMode (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionViewMode', val)
    },
    showLessonsPerWeek (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionShowLessonsPerWeek', val)
    },
    showActivities (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionShowActivities', val)
    },
    showSubjectsRequestInTable (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionShowSubjectsRequest', val)
    },
    showDatesInTable (val) {
      this.$helpers.setLocalStorage('doSubjectsDistributionShowDatesInTable', val)
    }
  },
  data () {
    return {
      subjectSearchQuery: '',
      selectedSubjectStatusFilters: [],
      sortBy: 'name',
      sortOrder: -1,
      isShowOnlyTeacherWithTeachingTime: false,
      isPrintUsers: false,
      editSubject: null,
      editSubjectPoolCalendarId: null,
      theEditTask: null,
      theUserToShowTasks: null,
      isSubjectModal: false,
      activeUserId: null,
      selectedKlassFilters: [],
      activeView: 'subjects',
      theZENToken: '',
      subjectsRequestEndDate: null,
      isUpdatingSchoolYear: false,
      isSubjectLoading: false,
      selectedSubjectFilters: [],
      selectedTypeFilters: [],
      selectedPersonFilters: [],
      groupTags: [],
      showLessonsPerWeek: false,
      showActivities: false,
      showSubjectsRequestInTable: false,
      showDatesInTable: false,
      viewMode: 'tiles',
      isDownloadingExcel: false,
      isPrintingTable: false,
      isZenExportModal: false,
      isEditingDates: false,
      selectedSubjectIds: [],
      bulkFromDate: null,
      bulkToDate: null,
      isSavingBulkDates: false
    }
  },
  computed: {
    isLessonsPerWeekActive () {
      return this.isSchoolDanish && this.showLessonsPerWeek
    },
    subjectDefaultDuration () {
      return this.school && this.school.subject_default_duration ? this.school.subject_default_duration : 45
    },
    noActiveSchoolYears () {
      return this.$store.state.noActiveSchoolYears
    },
    viewTabs () {
      const tabs = [
        { id: 'subjects', title: this.i18n[this.locale].subjects }
      ]
      if (this.useTasksFromSubjects) {
        tabs.push({ id: 'tasks_new', title: this.i18n[this.locale].tasks })
      } else {
        tabs.push({ id: 'tasks', title: this.i18n[this.locale].tasks })
      }
      if (this.isSchoolDanish) {
        tabs.push({ id: 'camps', title: this.i18n[this.locale].camps })
      }
      return tabs
    },
    selectedViewTabId () {
      return this.activeView
    },
    isTasksView () {
      return this.activeView === 'tasks'
    },
    isTasksNewView () {
      return this.activeView === 'tasks_new'
    },
    useTasksFromSubjects () {
      // const betaSchoolIds = [7272]
      return true
    },
    isTableView () {
      return this.viewMode === 'table'
    },
    isCampsView () {
      return this.activeView === 'camps'
    },
    subjectStatusFilters () {
      return [
        this.onlyShowSubjectsWithoutTeacherI18n,
        this.onlyShowSubjectsWithRequestsI18n,
        this.onlyShowSubjectsWithTeacherI18n,
        this.onlyShowSubjectsWithTeacherPoolI18n
      ]
    },
    user () {
      return this.$store.state.user
    },
    locale () {
      return this.$store.getters.locale
    },
    school () {
      return this.$store.state.activeSchool
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolCountry () {
      return this.school && this.school.country ? this.school.country : 'NO'
    },
    isSchoolDanish () {
      return this.schoolCountry.toUpperCase() === 'DK'
    },
    poolSubjectTypeLabel () {
      const title = this.$store.getters.localPoolTitle
      return title || 'Lokalpulje'
    },
    isShowSubjectsRequest () {
      return this.isSchoolDanish && this.userHasPermission('can_see_other_subject_requests')
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    userCanManageSchool () {
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions && this.userPermissions.indexOf('can_manage_school') > -1 || (this.user && this.user.admin)
      } else {
        return true
      }
    },
    i18n () {
      return I18N
    },
    zenUrl () {
      let startURL = 'https://zen.docendo.co/jwt_signin/?jwt='
      if (this.locale === 'da') {
        startURL = 'https://zen.docendo.co/da/jwt_signin/?jwt='
      }
      if (this.locale === 'nb') {
        startURL = 'https://zen.docendo.co/nb/jwt_signin/?jwt='
      }

      const schoolUuid = this.school && this.school.uuid ? this.school.uuid : null
      let url = startURL + this.theZENToken
      url += '&domain=' + encodeURIComponent(window.location.hostname)
      if (schoolUuid) {
        url += '&school_uuid=' + schoolUuid
      }

      return url
    },
    isConfirmDeletion () {
      return this.$store.state.subjects.isConfirmDeletion
    },
    itemToDelete () {
      return this.$store.state.subjects.itemToDelete
    },
    klassTags () {
      return this.klassCalendars.map(calendar => calendar.name)
    },
    uniqueSubjects () {
      const names = new Set()
      const cals = this.calsWithProps
      for (let i = 0; i < cals.length; i++) {
        const subjects = cals[i].vSubjects
        for (let j = 0; j < subjects.length; j++) {
          if (subjects[j].name) names.add(subjects[j].name)
        }
      }
      return [...names]
    },
    uniquePersons () {
      const names = new Set()
      const cals = this.calsWithProps
      for (let i = 0; i < cals.length; i++) {
        const subjects = cals[i].vSubjects
        for (let j = 0; j < subjects.length; j++) {
          subjects[j].vUserNames.forEach(name => names.add(name))
        }
      }
      return [...names]
    },
    allSelectedFiltersCount () {
      return this.selectedKlassFilters.length +
        this.selectedSubjectStatusFilters.length +
        this.selectedSubjectFilters.length +
        this.selectedTypeFilters.length +
        this.selectedPersonFilters.length
    },
    uniqueSubjectTypeLabels () {
      const typeLabels = {
        subject: 'Fag',
        activity: 'Aktivitet',
        available: 'Rådighedstime',
        person_resource: 'Ressource',
        person_resource_visible: 'Ressource (synlig)',
        resource: 'Ressource',
        additional_tasks: 'Øvrige opgaver',
        preparation: 'Forberedelse',
        pool: this.poolSubjectTypeLabel,
        club: 'SFO',
        without_kids: 'Uden børn',
        frame: 'Temavisning',
        optional: 'Valgfag/Hold',
        supplemental: 'UUV',
        break: 'Pause'
      }
      const labels = new Set()
      const cals = this.calsWithProps
      for (let i = 0; i < cals.length; i++) {
        const subjects = cals[i].vSubjects
        for (let j = 0; j < subjects.length; j++) {
          const type = subjects[j].subject_type
          if (type && type !== 'frame') {
            if (type === 'activity' && !this.showActivities) continue
            const label = typeLabels[type] || type
            labels.add(label)
          }
        }
      }
      return [...labels].sort((a, b) => a.localeCompare(b, 'da'))
    },
    showTeacherPoolHours () {
      return this.selectedSubjectStatusFilters.indexOf(this.onlyShowSubjectsWithTeacherPoolI18n) > -1
    },
    klassSubjects () {
      const map = {}
      const calendarsHash = this.allCalendarsInHash
      const subjectsHash = this.subjectsInHash
      const requestsHash = this.subjectsRequestsInHash
      const usersInCalHash = this.$store.getters.allUsersInCalIdHash
      const shouldExcludeTypes = !this.doesRessCountInKlassTotalHours
      const excludedTypes = ['resource', 'person_resource_visible']
      const translations = I18N[this.locale] || I18N.en

      const getExplicitTeacherPoolHours = (subject, calId) => {
        if (Array.isArray(subject.subject_calendars)) {
          const match = subject.subject_calendars.find(sc => sc && sc.calendar_id === calId && sc.hours != null)
          if (match) {
            const parsed = parseFloat(match.hours)
            return isNaN(parsed) ? null : parsed
          }
        }
        const userId = usersInCalHash[calId] && usersInCalHash[calId].id
        if (userId && Array.isArray(subject.teachers)) {
          const teacher = subject.teachers.find(t => t && t.user_id === userId && t.hours != null)
          if (teacher) {
            const parsed = parseFloat(teacher.hours)
            return isNaN(parsed) ? null : parsed
          }
        }
        return null
      }

      for (const [klassId, subjectIds] of Object.entries(this.subjectsInKlassHash)) {
        const klassIdInt = parseInt(klassId)
        const subjects = []
        let totalHours = 0
        let hasReductions = false

        for (let i = 0; i < subjectIds.length; i++) {
          const subjectId = subjectIds[i]
          const subject = subjectsHash[subjectId]
          if (!subject) continue

          let calendarHours = null
          if (subject.subject_calendars) {
            const match = subject.subject_calendars.find(sc => sc.calendar_id === klassIdInt)
            if (match) {
              calendarHours = parseFloat(match.hours)
            }
          }

          const fallbackHours = parseFloat(subject.hours)
          const vHours = isNaN(calendarHours) ? fallbackHours : calendarHours

          if (!isNaN(calendarHours) && calendarHours !== fallbackHours) {
            hasReductions = true
          }

          if (subject.subject_type === 'frame') continue

          if (!shouldExcludeTypes || excludedTypes.indexOf(subject.subject_type) < 0) {
            totalHours += isNaN(vHours) ? 0 : vHours
          }

          const calIds = subject.calendar_ids || []
          let hasTeachers = false
          let hasRooms = false
          const userNames = []
          const roomNames = []
          const teachersWithPool = []
          const otherKlasses = []

          for (let j = 0; j < calIds.length; j++) {
            const cal = calendarsHash[calIds[j]]
            if (!cal) continue
            if (cal.type === 'user') {
              hasTeachers = true
              if (cal.name) {
                userNames.push(cal.name)
                teachersWithPool.push({
                  name: cal.name,
                  hours: getExplicitTeacherPoolHours(subject, calIds[j])
                })
              }
            }
            if (cal.type === 'room') {
              hasRooms = true
              if (cal.name) roomNames.push(cal.name)
            }
            if (cal.type === 'klass' && parseInt(calIds[j]) !== klassIdInt) {
              otherKlasses.push(cal.name || '')
            }
          }

          const tooltip = [
            [translations.tooltip_teachers, userNames],
            [translations.tooltip_rooms, roomNames],
            [translations.tooltip_klasses, otherKlasses.filter(Boolean)]
          ]
            .filter(line => line[1].length > 0)
            .map(line => '<b>' + line[0] + ':</b> ' + line[1].map(this.escapeHtml).join(', '))
            .join('<br>')

          let remainingHours = null
          if (Array.isArray(subject.subject_calendars) && subject.cals) {
            let distributedSum = 0
            let userCalCount = 0
            let allUsersHaveExplicitHours = true
            subject.subject_calendars.forEach(sc => {
              const cal = subject.cals.find(c => c.id === sc.calendar_id)
              if (cal && cal.calendar_type === 'user') {
                userCalCount++
                if (sc.hours != null) {
                  distributedSum += parseFloat(sc.hours) || 0
                } else {
                  allUsersHaveExplicitHours = false
                }
              }
            })
            if (userCalCount > 0 && allUsersHaveExplicitHours) {
              const total = parseFloat(subject.hours) || 0
              const diff = total - distributedSum
              if (diff > 0) {
                remainingHours = diff
              }
            }
          }

          const requestedCalIds = requestsHash[subjectId] || []
          const vRequestedBy = requestedCalIds
            .filter(calId => calId !== this.userCalendarId && calendarsHash[calId])
            .map(calId => calendarsHash[calId] ? calendarsHash[calId].name : '')

          let hasTeacherPool = false
          if (Array.isArray(subject.teachers) && subject.teachers.some(teacher => teacher && teacher.hours != null)) {
            hasTeacherPool = true
          } else if (Array.isArray(subject.subject_calendars) && Array.isArray(subject.cals)) {
            hasTeacherPool = subject.subject_calendars.some(sc => {
              if (!sc || sc.hours == null) return false
              const cal = subject.cals.find(c => c && c.id === sc.calendar_id)
              return cal && cal.calendar_type === 'user'
            })
          }

          subjects.push({
            ...subject,
            vRequestedBy,
            vHasTeachers: hasTeachers,
            vHasRooms: hasRooms,
            vUserNames: userNames,
            vTooltip: tooltip,
            vTeachersWithPool: teachersWithPool,
            v_subjectOtherKlasses: otherKlasses,
            vHours,
            vLessonsPerWeek: this.calcLessonsPerWeek(subject.hours),
            vRemainingHours: remainingHours,
            vRemainingLessonsPerWeek: remainingHours != null ? this.calcLessonsPerWeek(remainingHours) : '0.0',
            vIsPartiallyDistributed: remainingHours != null && remainingHours > 0,
            vHasTeacherPool: hasTeacherPool
          })
        }

        map[klassId] = { subjects, totalHours, hasReductions }
      }

      return map
    },
    calsWithProps () {
      return this.klassCalendars.map(klass => {
        const data = this.klassSubjects[klass.id]
        return {
          ...klass,
          vTotalHours: data ? data.totalHours.toFixed(2) : '0.00',
          vHasReductions: data ? data.hasReductions : false,
          vSubjects: data ? data.subjects : []
        }
      })
    },
    filteredKlassCals () {
      let klasses = this.calsWithProps

      // Filtrer klasser baseret på valgte klasses
      if (this.selectedKlassFilters.length > 0) {
        klasses = klasses.filter(klass => this.selectedKlassFilters.includes(klass.name))
      }

      const hasStatusFilters = this.selectedSubjectStatusFilters.length > 0
      const hasSubjectFilters = this.selectedSubjectFilters.length > 0
      const hasTypeFilters = this.selectedTypeFilters.length > 0
      const hasPersonFilters = this.selectedPersonFilters.length > 0
      const hasSubjectLevelFilters = hasStatusFilters || hasSubjectFilters || hasTypeFilters || hasPersonFilters || !!this.subjectSearchQuery
      const typeLabelsMap = {
        subject: 'Fag',
        activity: 'Aktivitet',
        available: 'Rådighedstime',
        person_resource: 'Ressource',
        person_resource_visible: 'Ressource (synlig)',
        resource: 'Ressource',
        additional_tasks: 'Øvrige opgaver',
        preparation: 'Forberedelse',
        pool: this.poolSubjectTypeLabel,
        club: 'SFO',
        without_kids: 'Uden børn',
        frame: 'Temavisning',
        optional: 'Valgfag/Hold',
        supplemental: 'UUV',
        break: 'Pause'
      }

      return klasses.map(klass => ({
        ...klass,
        vSubjects: klass.vSubjects.filter(subject => {
          if (subject.subject_type === 'activity' && !this.showActivities) return false

          // Filtrering baseret på statusfiltre
          const hasFilterWithoutTeacher = this.selectedSubjectStatusFilters.includes(this.onlyShowSubjectsWithoutTeacherI18n)
          const hasFilterWithTeacher = this.selectedSubjectStatusFilters.includes(this.onlyShowSubjectsWithTeacherI18n)
          const hasFilterWithRequests = this.selectedSubjectStatusFilters.includes(this.onlyShowSubjectsWithRequestsI18n)
          const hasFilterWithTeacherPool = this.selectedSubjectStatusFilters.includes(this.onlyShowSubjectsWithTeacherPoolI18n)

          const matchesStatusFilter = !hasStatusFilters || (
            (hasFilterWithoutTeacher && (!subject.vHasTeachers || subject.vIsPartiallyDistributed)) ||
            (hasFilterWithTeacher && subject.vHasTeachers) ||
            (hasFilterWithRequests && subject.vRequestedBy.length > 0) ||
            (hasFilterWithTeacherPool && subject.vHasTeacherPool)
          )

          // Filtrering baseret på søgefeltet
          const subjectName = subject.name ? subject.name.toLowerCase() : ''
          const matchesSearch = !this.subjectSearchQuery || subjectName.includes(this.subjectSearchQuery.toLowerCase())

          // Filtrering baseret på valgte fag
          const matchesSubjectFilter = !hasSubjectFilters || this.selectedSubjectFilters.includes(subject.name)

          // Filtrering baseret på valgt type
          const typeLabel = typeLabelsMap[subject.subject_type] || subject.subject_type || ''
          const matchesTypeFilter = !hasTypeFilters || this.selectedTypeFilters.includes(typeLabel)

          const matchesPersonFilter = !hasPersonFilters || subject.vUserNames.some(name => this.selectedPersonFilters.includes(name))

          return matchesStatusFilter && matchesSearch && matchesSubjectFilter && matchesTypeFilter && matchesPersonFilter
        }).sort((a, b) => {
          const nameA = (a.name || '').toLowerCase()
          const nameB = (b.name || '').toLowerCase()
          return nameA.localeCompare(nameB, 'da')
        })
      })).filter(klass => !hasSubjectLevelFilters || klass.vSubjects.length > 0)
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    isLoading () {
      return this.$store.state.subjects.isLoading
    },
    klassCalendars () {
      return this.$store.getters.klassCalendars
    },
    usersAssignedHoursHash () {
      return this.$store.getters.usersAssignedHoursHash
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    allUsersInCalendarHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    subjectsInKlassHash () {
      return this.$store.getters.subjectsInKlassHash
    },
    subjectsInUserHash () {
      return this.$store.getters.subjectsInUserHash
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    tasksInUserHash () {
      return this.$store.getters.tasksInUserHash
    },
    tasksInHash () {
      return this.$store.getters.tasksInHash
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    usersFilteredWithTags () {
      const users = this.$store.getters.usersFilteredWithTags
      if (!this.isPoolsDeactivated && this.isShowOnlyTeacherWithTeachingTime) {
        return users.filter(user => {
          const userHours = this.usersAssignedHoursHash[user.id]
          return userHours && userHours.assigned_teaching_hours
        })
      }
      return users
    },
    userTeachingDiffMap () {
      const map = {}
      const usersHash = this.allUsersInHash
      const subjectsUserHash = this.subjectsInUserHash
      const subjectsHash = this.subjectsInHash
      const poolsDeactivated = this.isPoolsDeactivated
      const assignedHash = this.usersAssignedHoursHash

      for (const user of this.usersFilteredWithTags) {
        const userCalId = usersHash[user.id] ? usersHash[user.id].user_calendar_id : null
        if (!userCalId) { map[user.id] = '0.0'; continue }

        let total = 0
        const sIds = subjectsUserHash[userCalId]
        if (sIds) {
          for (let i = 0; i < sIds.length; i++) {
            const s = subjectsHash[sIds[i]]
            if (!s) continue
            total += this.getSubjectHours(s.id, userCalId)
          }
        }

        const assigned = assignedHash[user.id] ? assignedHash[user.id].assigned_teaching_hours : 0
        const diff = !poolsDeactivated ? assigned - total : total
        map[user.id] = diff.toFixed(1)
      }
      return map
    },
    userLessonsPerWeekMap () {
      const map = {}
      const usersHash = this.allUsersInHash
      const subjectsUserHash = this.subjectsInUserHash
      const subjectsHash = this.subjectsInHash

      for (const user of this.usersFilteredWithTags) {
        const userCalId = usersHash[user.id] ? usersHash[user.id].user_calendar_id : null
        if (!userCalId) { map[user.id] = '0.0'; continue }

        let total = 0
        const sIds = subjectsUserHash[userCalId] || []
        for (let i = 0; i < sIds.length; i++) {
          const s = subjectsHash[sIds[i]]
          if (!s) continue
          const hours = this.getSubjectHoursForUser(s, userCalId)
          total += parseFloat(this.calcLessonsPerWeek(hours))
        }

        map[user.id] = total.toFixed(1)
      }
      return map
    },
    sortedUsersFilteredWithTags () {
      if (this.sortBy === 'name') {
        return this.$helpers.sortArrOnName(this.usersFilteredWithTags)
      }
      const diffMap = this.userTeachingDiffMap
      return [...this.usersFilteredWithTags].sort((a, b) => this.sortOrder < 0 ? diffMap[a.id] - diffMap[b.id] : diffMap[b.id] - diffMap[a.id])
    },
    selectedGroupTags () {
      return this.$store.state.selectedTags || []
    },
    schoolSettings () {
      return this.$store.state.schoolSettings
    },
    isPoolsDeactivated () {
      return this.schoolSettings && this.schoolSettings['deactivatePools'] ? this.schoolSettings['deactivatePools'] : false
    },
    doesRessCountInKlassTotalHours () {
      return !this.schoolSettings.hasOwnProperty('hide_ressource_hours_count_in_total_hours_for_klasse')
    },
    subjectsRequestsInHash () {
      return this.$store.getters.subjectsRequestsInHash
    },
    isShowSubjectsWithoutTeachers () {
      return this.selectedSubjectStatusFilters.indexOf(this.onlyShowSubjectsWithoutTeacherI18n) > -1
    },
    isShowSubjectsWithTeachers () {
      return this.selectedSubjectStatusFilters.indexOf(this.onlyShowSubjectsWithTeacherI18n) > -1
    },
    onlyShowSubjectsWithoutTeacherI18n () {
      return this.i18n[this.locale].only_subjects_without_teachers
    },
    onlyShowSubjectsWithRequestsI18n () {
      return this.i18n[this.locale].only_subjects_with_requests
    },
    onlyShowSubjectsWithTeacherI18n () {
      return this.i18n[this.locale].only_subjects_with_teachers
    },
    onlyShowSubjectsWithTeacherPoolI18n () {
      return this.i18n[this.locale].only_subjects_with_teacher_pool
    },
    visibleSubjectIdsForBulk () {
      const ids = new Set()
      this.filteredKlassCals.forEach(kc => {
        kc.vSubjects.forEach(s => ids.add(s.id))
      })
      return [...ids]
    },
    allVisibleSelected () {
      const visible = this.visibleSubjectIdsForBulk
      return visible.length > 0 && visible.every(id => this.selectedSubjectIds.indexOf(id) >= 0)
    },
    bulkDateMin () {
      if (this.activeSchoolYear && this.activeSchoolYear.employee_time_start) {
        return moment(this.activeSchoolYear.employee_time_start)
      }
      return null
    },
    bulkDateMax () {
      if (this.activeSchoolYear && this.activeSchoolYear.employee_time_end) {
        return moment(this.activeSchoolYear.employee_time_end)
      }
      return null
    }
  },
  methods: {
    escapeHtml (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    onSelectView (tabId) {
      this.activeView = tabId
    },
    userHasPermission (permission) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions && this.userPermissions.indexOf(permission) > -1 || this.user.admin
      } else {
        return true
      }
    },
    getSubjectedRequestedBy (subjectId) {
      const currentCalendarIds = this.subjectsRequestsInHash[subjectId] || []
      return currentCalendarIds.filter(calId => calId !== this.userCalendarId && this.allCalendarsInHash[calId]).map(calId => this.allCalendarsInHash[calId] ? this.allCalendarsInHash[calId]['name'] : [])
    },
    setSubjectsRequestEndDate (schoolYear) {
      if (schoolYear && Object.prototype.hasOwnProperty.call(schoolYear, 'subject_request_end')) {
        this.subjectsRequestEndDate = schoolYear.subject_request_end
      }
    },
    onSetSubjectsRequestEndDate (date) {
      this.isUpdatingSchoolYear = true
      let dbDate = null
      if (date) {
        dbDate = moment(date).format('YYYY-MM-DD')
      }
      docendo.schoolYearsUpdate(
        this.schoolId,
        this.activeSchoolYear.id,
        {subject_request_end: dbDate},
        success => {
          this.isUpdatingSchoolYear = false
          this.setSubjectsRequestEndDate(success)
          this.updateRORDOMAnnouncementBadge(dbDate)
        },
        failure => {
          console.log('failure', failure)
        }
      )
    },
    updateRORDOMAnnouncementBadge (dbDate) {
      const announcementEl = document.querySelector('.do-top-menu-announcement')
      const navStart = document.querySelector('#nav-start')
      const navEnd = document.querySelector('#nav-end')

      if (!navStart || !navEnd) {
        console.warn('Kunne ikke finde navigationselementerne i headeren.')
        return
      }

      // Hvis dbDate er før i dag, fjern elementet hvis det findes, og stop her
      if (dbDate && moment(dbDate).isBefore(moment(), 'day')) {
        if (announcementEl) {
          announcementEl.remove()
        }
        return
      }

      if (!dbDate) {
        // Hvis dbDate == null og elementet eksisterer → fjern det
        if (announcementEl) {
          announcementEl.remove()
        }
        return
      }

      if (!announcementEl) {
        // Hvis elementet ikke eksisterer, opret det
        const newEl = document.createElement('div')
        newEl.classList.add('do-top-menu-announcement')

        const anchor = document.createElement('a')
        anchor.href = `/schools/${this.schoolId}/subject_requests?school_year_id=${this.activeSchoolYear.id}`
        anchor.innerHTML = `Fagønsker er åben indtil <span data-date="${dbDate}">${moment(dbDate).format('D. MMMM YYYY')}</span>`

        newEl.appendChild(anchor)

        // Indsæt den mellem navStart og navEnd
        navStart.parentNode.insertBefore(newEl, navEnd)
      } else {
        // Hvis elementet eksisterer, opdater <span> med den nye dato
        const spanEl = announcementEl.querySelector('span[data-date]')
        if (spanEl) {
          spanEl.setAttribute('data-date', dbDate)
          spanEl.textContent = moment(dbDate).format('D. MMMM YYYY')
        }
      }
    },
    readSubjectRequests () {
      if (this.isShowSubjectsRequest && this.activeSchoolYear && this.activeSchoolYear.id) {
        this.$store.dispatch('readSubjectRequests', this.activeSchoolYear.id)
      }
    },
    calcLessonsPerWeek (hours) {
      return this.$helpers.calcLessonsPerWeek(hours, this.subjectDefaultDuration)
    },
    toggleDoesRessCount () {
      if (!this.doesRessCountInKlassTotalHours) {
        this.$store.dispatch('deleteSchoolSetting', 'hide_ressource_hours_count_in_total_hours_for_klasse')
      } else {
        this.$store.dispatch('updateSchoolSettings', {'hide_ressource_hours_count_in_total_hours_for_klasse': true})
      }
    },
    closeIsPrintUsers () {
      this.isPrintUsers = false
      window.scrollTo(0, 0)
    },
    deleteSubject (editSubject) {
      let deletedSubject = {
        id: editSubject.id,
        school_id: editSubject.school_id,
        school_year_id: this.activeSchoolYear.id
      }
      deletedSubject['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('deleteSubject', deletedSubject)
      this.closeModal()
    },
    onZenExported () {
      this.isZenExportModal = false
    },
    userHasAgreements (userId) {
      return this.usersAssignedHoursHash[userId] && this.usersAssignedHoursHash[userId].agreements
    },
    setSortBy (key) {
      this.sortBy = key
      this.sortOrder = this.sortOrder * -1
    },
    confirmDelete () {
      if (this.itemToDelete && this.itemToDelete.itemType === 'task') {
        this.$store.dispatch('deleteTask', this.itemToDelete)
        this.$store.commit('setTheDeletedItemId', this.itemToDelete.id)
      }
      this.$store.commit('setConfirmDeletion', false)
      this.$store.commit('setItemToDelete', null)
    },
    subjectHasTeachers (subjectId) {
      let calIds = this.subjectsInHash[subjectId] ? this.subjectsInHash[subjectId].calendar_ids : []
      let hasTeachers = false
      calIds.forEach(calId => {
        if (this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'user') {
          hasTeachers = true
        }
      })
      return hasTeachers
    },
    subjectHasRooms (subjectId) {
      let calIds = this.subjectsInHash[subjectId] ? this.subjectsInHash[subjectId].calendar_ids : []
      let hasRooms = false
      calIds.forEach(calId => {
        if (this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'room') {
          hasRooms = true
        }
      })
      return hasRooms
    },
    getUserNames (subjectId) {
      let calIds = this.subjectsInHash[subjectId] ? this.subjectsInHash[subjectId].calendar_ids : []
      if (!calIds || calIds.length < 1 || !calIds[0]) {
        return ''
      }
      return calIds.filter(calId => this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'user').map(userCalId => this.allCalendarsInHash[userCalId].name ? this.allCalendarsInHash[userCalId].name : '')
    },
    subjectHasOtherKlasses (subjectId, currentKlassId) {
      let calIds = this.subjectsInHash[subjectId] ? this.subjectsInHash[subjectId].calendar_ids : []
      const otherKlasses = []
      calIds.forEach(calId => {
        if (this.allCalendarsInHash[calId] && this.allCalendarsInHash[calId].type === 'klass' && parseInt(calId) !== parseInt(currentKlassId)) {
          if (this.allCalendarsInHash[calId].name) {
            otherKlasses.push(this.allCalendarsInHash[calId].name)
          } else {
            otherKlasses.push('')
          }
        }
      })
      return otherKlasses
    },
    subjectHasRoom (subjectId) {
      let subject = this.subjectsInHash[subjectId]
      return subject.rooms_calendar_ids && subject.rooms_calendar_ids.length > 0 && subject.rooms_calendar_ids[0]
    },
    updateLocalStorage (key, filters) {
      const val = JSON.stringify(filters)
      this.$helpers.setLocalStorage(key, val)
    },
    toggleFilter (filter, filterArray, storageKey = null) {
      if (filterArray.includes(filter)) {
        filterArray.splice(filterArray.indexOf(filter), 1)
      } else {
        filterArray.push(filter)
      }

      if (storageKey) {
        this.updateLocalStorage(storageKey, filterArray)
      }
    },
    toggleKlassTag (tag) {
      this.toggleFilter(tag, this.selectedKlassFilters, 'doSubjectsDistributionSubjectKlassFilters')
    },
    clearSelectedKlassFilters () {
      this.selectedKlassFilters = []
      const storageKey = 'doSubjectsDistributionSubjectKlassFilters'
      this.updateLocalStorage(storageKey, [])
    },
    toggleSelectedSubjectStatusFilters (filter) {
      this.toggleFilter(filter, this.selectedSubjectStatusFilters, 'doSubjectsDistributionSubjectStatusFilters')
    },
    toggleSelectedSubjectFilters (filter) {
      this.toggleFilter(filter, this.selectedSubjectFilters, 'doSubjectsDistributionSubjectFilters')
    },
    clearSelectedSubjectFilters () {
      this.selectedSubjectFilters = []
      const storageKey = 'doSubjectsDistributionSubjectFilters'
      this.updateLocalStorage(storageKey, [])
    },
    clearSelectedSubjectStatusFilters () {
      this.selectedSubjectStatusFilters = []
      const storageKey = 'doSubjectsDistributionSubjectStatusFilters'
      this.updateLocalStorage(storageKey, [])
    },
    toggleSelectedTypeFilters (filter) {
      this.toggleFilter(filter, this.selectedTypeFilters, 'doSubjectsDistributionSubjectTypeFilters')
    },
    clearSelectedTypeFilters () {
      this.selectedTypeFilters = []
      this.updateLocalStorage('doSubjectsDistributionSubjectTypeFilters', [])
    },
    toggleSelectedPersonFilters (filter) {
      this.toggleFilter(filter, this.selectedPersonFilters, 'doSubjectsDistributionSubjectPersonFilters')
    },
    clearSelectedPersonFilters () {
      this.selectedPersonFilters = []
      this.updateLocalStorage('doSubjectsDistributionSubjectPersonFilters', [])
    },
    clearAllFilters () {
      this.clearSelectedKlassFilters()
      this.clearSelectedSubjectStatusFilters()
      this.clearSelectedSubjectFilters()
      this.clearSelectedTypeFilters()
      this.clearSelectedPersonFilters()
    },
    toggleGroupTag (tag) {
      this.$store.commit('setSelectedTags', tag)
      this.updateLocalStorage('doSubjectsDistributionSelectedGroupTags', this.$store.state.selectedTags)
    },
    clearSelectedGroupTags () {
      const tagsToRemove = [...this.$store.state.selectedTags]
      tagsToRemove.forEach(tag => {
        this.$store.commit('setSelectedTags', tag)
      })
      this.updateLocalStorage('doSubjectsDistributionSelectedGroupTags', [])
    },
    loadSelectedGroupTagsFromLocalStorage () {
      const stored = this.$helpers.getLocalStorage('doSubjectsDistributionSelectedGroupTags')
      if (!stored) return
      let parsed = []
      try {
        parsed = JSON.parse(stored)
      } catch (e) {
        parsed = []
      }
      if (!Array.isArray(parsed)) return

      const current = [...this.$store.state.selectedTags]
      current.forEach(tag => {
        if (parsed.indexOf(tag) < 0) {
          this.$store.commit('setSelectedTags', tag)
        }
      })
      parsed.forEach(tag => {
        if (this.$store.state.selectedTags.indexOf(tag) < 0) {
          this.$store.commit('setSelectedTags', tag)
        }
      })
    },
    getUserAssignedTeachingHours (userId) {
      return this.usersAssignedHoursHash[userId] ? this.usersAssignedHoursHash[userId].assigned_teaching_hours : 0
    },
    getSubjectHours (subjectId, userCalId) {
      const subject = this.subjectsInHash[subjectId]
      if (!subject) return 0

      // 1. Forsøg at finde timer i brugerens egen kalender
      if (Array.isArray(subject.subject_calendars)) {
        const userMatch = subject.subject_calendars.find(sc =>
          sc.calendar_id === userCalId && sc.hours != null
        )
        if (userMatch) {
          const parsed = parseFloat(userMatch.hours)
          return isNaN(parsed) ? 0 : parsed
        }
      }

      // 2. Hvis ikke, find første klass med timer
      if (Array.isArray(subject.cals)) {
        const klass = subject.cals.find(c => c.calendar_type === 'klass')
        if (klass && Array.isArray(subject.subject_calendars)) {
          const klassMatch = subject.subject_calendars.find(sc =>
            sc.calendar_id === klass.id && sc.hours != null
          )
          if (klassMatch) {
            const parsed = parseFloat(klassMatch.hours)
            return isNaN(parsed) ? 0 : parsed
          }
        }
      }

      // 3. Fallback til subject.hours
      const fallback = parseFloat(subject.hours)
      return isNaN(fallback) ? 0 : fallback
    },
    getSubjectHoursForUser (subject, userCalId) {
      if (Array.isArray(subject.subject_calendars)) {
        const userMatch = subject.subject_calendars.find(sc =>
          sc.calendar_id === userCalId && sc.hours != null
        )
        if (userMatch) {
          const parsed = parseFloat(userMatch.hours)
          if (!isNaN(parsed)) return parsed
        }
      }
      const fallback = parseFloat(subject.hours)
      return isNaN(fallback) ? 0 : fallback
    },
    getTotalSubjectTeachingHours (userCalId) {
      const ids = this.subjectsInUserHash[userCalId] || []
      return ids.reduce((acc, id) => acc + (this.getSubjectHours(id, userCalId) * 1 || 0), 0)
    },
    getTotalUserHoursTeachingCount (userCalId) {
      return this.getTotalSubjectTeachingHours(userCalId)
    },
    getUserTeachingHoursDiff (userId) {
      const diff = !this.isPoolsDeactivated ? this.getUserAssignedTeachingHours(userId) - this.getTotalUserHoursTeachingCount(this.allUsersInHash[userId].user_calendar_id) : this.getTotalUserHoursTeachingCount(this.allUsersInHash[userId].user_calendar_id)
      return diff.toFixed(1)
    },
    getUserLessonsPerWeek (userId) {
      const user = this.allUsersInHash[userId]
      if (!user || !user.user_calendar_id) {
        return '0.0'
      }
      const userCalId = user.user_calendar_id
      const subjectsUserHash = this.subjectsInUserHash
      const subjectsHash = this.subjectsInHash

      let total = 0
      const sIds = subjectsUserHash[userCalId] || []
      for (let i = 0; i < sIds.length; i++) {
        const s = subjectsHash[sIds[i]]
        if (!s) continue
        const hours = this.getSubjectHoursForUser(s, userCalId)
        total += parseFloat(this.calcLessonsPerWeek(hours))
      }

      return total.toFixed(1)
    },
    setActiveUserId (userId) {
      if (this.activeUserId === userId) {
        return
      }
      this.activeUserId = userId
    },
    resetActiveUserId () {
      setTimeout(_ => {
        this.activeUserId = null
      }, 0)
    },
    setTheUserToShowTasks (user) {
      this.theUserToShowTasks = user
    },
    closeTheUserToShowTasks () {
      this.theUserToShowTasks = null
    },
    getAllUserSubjects (calid) {
      return this.subjectsInUserHash[calid]
    },
    readAssignedHours () {
      let schoolObj = {
        schoolId: this.schoolId,
        schoolYearId: this.activeSchoolYear.id
      }
      this.$store.dispatch('getUsers', this.schoolId, null)
      this.$store.dispatch('readUsersAssignedHours', schoolObj)
    },
    totalHoursCount (klassCalId) {
      let subjectsHours = [0]
      const shouldExcludeTypes = !this.doesRessCountInKlassTotalHours
      const excludedTypes = ['resource', 'person_resource_visible']

      if (this.subjectsInKlassHash[klassCalId]) {
        const subjects = this.subjectsInKlassHash[klassCalId]
          .map(subjectId => this.subjectsInHash[subjectId])
          .filter(subject => subject.subject_type !== 'frame' && (!shouldExcludeTypes || excludedTypes.indexOf(subject.subject_type) < 0))

        subjectsHours = subjects.map(subject => {
          let calendarHours = null
          if (subject.subject_calendars) {
            const match = subject.subject_calendars.find(sc => sc.calendar_id === klassCalId)
            if (match) {
              calendarHours = parseFloat(match.hours)
            }
          }

          const fallbackHours = parseFloat(subject.hours)
          const vHours = isNaN(calendarHours) ? fallbackHours : calendarHours
          return isNaN(vHours) ? 0 : vHours
        })
      }

      const sum = subjectsHours.reduce((acc, item) => acc + item, 0)
      return sum.toFixed(2)
    },
    isShowWithoutTeacher (subjectid) {
      if (!this.isShowSubjectsWithoutTeachers) {
        return true
      } else {
        return !this.subjectHasTeachers(subjectid)
      }
    },
    closeModal () {
      this.editSubject = null
      this.editSubjectPoolCalendarId = null
      this.isSubjectModal = false
    },
    setEditSubject (editSubject, klassCalId) {
      this.editSubjectPoolCalendarId = klassCalId || null
      this.isSubjectLoading = true
      this.isSubjectModal = true
      docendo.showSubject(editSubject.id, success => {
        const pendingTab = this._pendingTab
        this._pendingTab = null
        this._pendingSubjectId = null
        this.editSubject = pendingTab
          ? Object.assign({}, success, { _openTab: pendingTab })
          : Object.assign({}, success)
        this.isSubjectLoading = false
        setTimeout(_ => {
          this.$store.commit('updateSubject', success)
        }, 1500)
        setTimeout(_ => {
          this.isTheCreateCalenderSubjectActive = null
        }, 1)
      }, failure => {
        console.log('failure')
      })
    },
    setEditTask (editTask) {
      this.theEditTask = this.tasksInHash[editTask.id]
    },
    updateSubject (updatedSubject) {
      if (!updatedSubject['schoolId']) {
        updatedSubject['schoolId'] = this.schoolId
      }
      if (!updatedSubject['schoolYearId']) {
        updatedSubject['schoolYearId'] = this.activeSchoolYear.id
      }
      this.$store.dispatch('updateSubject', updatedSubject)
      this.closeModal()
    },
    saveAndShowTab (updatedSubject, tab) {
      const poolCalendarId = this.editSubjectPoolCalendarId
      this.updateSubject(updatedSubject)
      this.closeModal()
      this._pendingTab = tab
      this._pendingSubjectId = updatedSubject.id
      setTimeout(() => {
        this.setEditSubject({ id: this._pendingSubjectId }, poolCalendarId)
      }, 500)
    },
    toggleEditDates () {
      if (this.isEditingDates) {
        this.cancelEditDates()
      } else {
        this.isEditingDates = true
        this.selectedSubjectIds = []
        this.bulkFromDate = this.bulkDateMin ? this.bulkDateMin.clone() : moment()
        this.bulkToDate = this.bulkDateMax ? this.bulkDateMax.clone() : moment()
      }
    },
    cancelEditDates () {
      this.isEditingDates = false
      this.selectedSubjectIds = []
      this.isSavingBulkDates = false
    },
    toggleSubjectSelection (subjectId) {
      const idx = this.selectedSubjectIds.indexOf(subjectId)
      if (idx >= 0) {
        this.selectedSubjectIds.splice(idx, 1)
      } else {
        this.selectedSubjectIds.push(subjectId)
      }
    },
    toggleSelectAllVisible () {
      if (this.allVisibleSelected) {
        this.selectedSubjectIds = []
      } else {
        this.selectedSubjectIds = this.visibleSubjectIdsForBulk.slice()
      }
    },
    setBulkFromDate (date) {
      this.bulkFromDate = date
    },
    setBulkToDate (date) {
      this.bulkToDate = date
    },
    saveBulkDates () {
      if (this.selectedSubjectIds.length === 0 || this.isSavingBulkDates) return
      const fromMoment = moment(this.bulkFromDate)
      const toMoment = moment(this.bulkToDate)
      if (this.bulkDateMin && fromMoment.isBefore(this.bulkDateMin, 'day')) {
        eventbus.toast = { text: 'Startdato skal være inden for skoleåret', success: false }
        return
      }
      if (this.bulkDateMax && toMoment.isAfter(this.bulkDateMax, 'day')) {
        eventbus.toast = { text: 'Slutdato skal være inden for skoleåret', success: false }
        return
      }
      const changes = {
        from_date: fromMoment.format('YYYY-MM-DD'),
        to_date: toMoment.format('YYYY-MM-DD')
      }
      this.isSavingBulkDates = true
      docendo.bulkUpdateSubjects(this.schoolId, this.selectedSubjectIds.slice(), changes, data => {
        const subjects = typeof data === 'string' ? JSON.parse(data) : data
        subjects.forEach(subj => {
          subj.calendar_ids = subj.cals ? subj.cals.map(c => c.id) : []
          this.$store.commit('updateSubject', subj)
        })
        this.isSavingBulkDates = false
        eventbus.toast = { text: 'Datoer opdateret', success: true }
        this.cancelEditDates()
      }, err => {
        this.isSavingBulkDates = false
        const msg = err && err.data && err.data.error ? err.data.error : (err && err.error ? err.error : 'Kunne ikke opdatere datoer')
        eventbus.toast = { text: msg, success: false }
      })
    },
    setPrint () {
      this.isPrintUsers = true
    },
    getZENJWT () {
      docendo.getJwt(data => {
        this.theZENToken = data && data.token ? data.token : ''
      })
    },
    readSubject () {
      this.$store.dispatch('readSubjects', this.activeSchoolYear.id)
    },
    loadFiltersFromLocalStorage (key, filterArray) {
      const deprecatedFilters = ['Vis kun fag uden underviser']
      const storedFilters = this.$helpers.getLocalStorage(key)

      if (storedFilters) {
        let parsedFilters = JSON.parse(storedFilters)

        // Fjern eventuelle forældede filtre
        parsedFilters = parsedFilters.filter(filter => !deprecatedFilters.includes(filter))

        this[filterArray] = parsedFilters

        // Opdater localStorage, hvis der blev fjernet noget
        if (parsedFilters.length !== JSON.parse(storedFilters).length) {
          this.$helpers.setLocalStorage(key, JSON.stringify(parsedFilters))
        }
      }
    },
    printTable () {
      this.isPrintingTable = true
      this.$nextTick(() => {
        window.print()
        this.isPrintingTable = false
      })
    },
    asyncLoadXLSXAndDownloadTable () {
      this.isDownloadingExcel = true
      if (window.XLSX) {
        this.downloadTableToExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(() => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            this.downloadTableToExcel()
          }
        }, 500)
      }
    },
    downloadTableToExcel () {
      const XLSX = window.XLSX
      const typeLabels = {
        subject: 'Fag',
        activity: 'Aktivitet',
        available: 'Rådighedstime',
        person_resource: 'Ressource',
        person_resource_visible: 'Ressource (synlig)',
        resource: 'Ressource',
        additional_tasks: 'Øvrige opgaver',
        preparation: 'Forberedelse',
        pool: this.poolSubjectTypeLabel,
        club: 'SFO',
        without_kids: 'Uden børn',
        frame: 'Temavisning',
        optional: 'Valgfag/Hold',
        supplemental: 'UUV',
        break: 'Pause'
      }

      const rows = []
      const klasses = this.filteredKlassCals
      const showRequested = this.showSubjectsRequestInTable && this.isShowSubjectsRequest && Object.keys(this.subjectsRequestsInHash).length > 0
      const showDates = this.showDatesInTable

      const header = ['Klasse', 'Fag', 'Type', 'Personale']
      if (showDates) header.push('Startdato', 'Slutdato')
      if (showRequested) header.push('Ønsket af')
      header.push('Lokaler', 'Klasser', 'Lek. pr. uge', 'Timer')
      rows.push(header)

      for (let k = 0; k < klasses.length; k++) {
        const klass = klasses[k]
        const subjects = [...klass.vSubjects].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'da'))

        for (let s = 0; s < subjects.length; s++) {
          const subj = subjects[s]

          const teachers = subj.vUserNames && subj.vUserNames.length ? subj.vUserNames.join(', ') : ''

          const requestedBy = subj.vRequestedBy && subj.vRequestedBy.length ? subj.vRequestedBy.join(', ') : ''

          let rooms = ''
          if (subj.cals && subj.cals.length) {
            rooms = subj.cals
              .filter(function (cal) { return cal.calendar_type === 'room' })
              .map(function (cal) { return cal.name })
              .join(', ')
          }

          const otherKlasses = subj.v_subjectOtherKlasses && subj.v_subjectOtherKlasses.length
            ? subj.v_subjectOtherKlasses.join(', ')
            : ''

          let hours = subj.vHours
          if (subj.subject_calendars && subj.subject_calendars.length) {
            const match = subj.subject_calendars.find(function (sc) { return sc.calendar_id === klass.id })
            if (match && match.hours != null) {
              hours = parseFloat(match.hours)
            }
          }

          const row = [
            klass.name || '',
            subj.name || '',
            typeLabels[subj.subject_type] || subj.subject_type || '',
            teachers
          ]
          if (showDates) {
            row.push(
              subj.from_date ? moment(subj.from_date).format('D. MMM YYYY') : '',
              subj.to_date ? moment(subj.to_date).format('D. MMM YYYY') : ''
            )
          }
          if (showRequested) row.push(requestedBy)
          row.push(
            rooms,
            otherKlasses,
            parseFloat(subj.vLessonsPerWeek) || 0,
            isNaN(hours) ? 0 : parseFloat(hours)
          )
          rows.push(row)
        }
      }

      const ws = XLSX.utils.aoa_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Fagfordeling')
      XLSX.writeFile(wb, 'docendo_fagfordeling.xlsx')
      this.isDownloadingExcel = false
    }
  },
  beforeMount () {
    // Hent tags for grupper
    if (this.schoolId) {
      docendo.schoolTags(this.schoolId, data => {
        this.groupTags = Array.isArray(data) ? data : []
      }, failure => {
        this.groupTags = []
      })
    }

    this.loadFiltersFromLocalStorage('doSubjectsDistributionSubjectStatusFilters', 'selectedSubjectStatusFilters')
    this.loadFiltersFromLocalStorage('doSubjectsDistributionSubjectKlassFilters', 'selectedKlassFilters')
    this.loadFiltersFromLocalStorage('doSubjectsDistributionSubjectFilters', 'selectedSubjectFilters')
    this.loadFiltersFromLocalStorage('doSubjectsDistributionSubjectTypeFilters', 'selectedTypeFilters')
    this.loadFiltersFromLocalStorage('doSubjectsDistributionSubjectPersonFilters', 'selectedPersonFilters')
    this.loadSelectedGroupTagsFromLocalStorage()

    const storedViewMode = this.$helpers.getLocalStorage('doSubjectsDistributionViewMode')
    if (storedViewMode === 'tiles' || storedViewMode === 'table') {
      this.viewMode = storedViewMode
    }
    const storedLessons = this.$helpers.getLocalStorage('doSubjectsDistributionShowLessonsPerWeek')
    if (storedLessons === 'true') {
      this.showLessonsPerWeek = true
    }
    const storedActivities = this.$helpers.getLocalStorage('doSubjectsDistributionShowActivities')
    if (storedActivities === 'true') {
      this.showActivities = true
    }
    const storedSubjectsRequest = this.$helpers.getLocalStorage('doSubjectsDistributionShowSubjectsRequest')
    if (storedSubjectsRequest === 'true') {
      this.showSubjectsRequestInTable = true
    }
    const storedDates = this.$helpers.getLocalStorage('doSubjectsDistributionShowDatesInTable')
    if (storedDates === 'true') {
      this.showDatesInTable = true
    }
  },
  mounted () {
    this.readAssignedHours()
    this.getZENJWT()
    this.readSubjectRequests()
    window.addEventListener('new_subjects_created', () => this.readSubject())
  },
  beforeDestroy () {
    window.removeEventListener('new_subjects_created', () => this.readSubject())
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.subject-dist {
  min-height: 50vh;
}

.subject-dist > .subject-dist__header {
  display: flex;
  justify-content: space-between;
}

.subject-dist > .subject-dist__header h1 {
  margin-right: 10px;
}

.subject-dist__header__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subject-dist__header, .doTable__users__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 50px;
  position: sticky;
  top: 0;
  z-index: 10;
  background:#edf0f5;
}

.subject-dist__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.subject-dist__body {
  display: flex;
  height: calc(100vh - 120px);
}

.subject-dist__body > .doTable__users {
  min-width: 300px;
  max-width: 300px;
  margin-right: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
}

.subjects-and-tasks {
  width: 100%;
  overflow-y: auto;
  padding-left: 4px;
  margin-left: -4px;
}

.doTable {
  flex: 1;
  border-radius: 4px;
}

ul.doTable__items {
  margin: 0;
}

.doTable__klass {
  margin: 0;
}

.doTable__klass__header {
  display: flex;
  justify-content: space-between;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  text-transform: uppercase;
  padding: 8px;
  font-weight: bold;
}

.doTable__klass__header label {
  color: #c0c0c0;
}

.subjects {
  display: flex;
  flex-wrap: wrap;
  padding: 10px;
  margin: 0;
  border: 1px solid #e2e8f0;
  border-width: 0 1px 0 1px;
  background-color: #ffffff;
}

.subject-remaining {
  display: inline;
  font-size: 9px;
  font-weight: 600;
  padding: 1px 4px;
  margin-left: 2px;
  vertical-align: middle;
  white-space: nowrap;
}

.subjects_subject {
  position: relative;
  top: 4px;
  left: 2px;
  right: 2px;
  bottom: 0;
  padding: 4px 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, .3);
  text-align: left;
  margin: 0px 10px 10px 0;
  min-width: auto;
  font-weight: bold;
  transition: transform 0.25s;
  animation: fadeIn;
  cursor: pointer;
}

.subjects_subject.subject--has-request:before {
  content: "\e725";
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.subjects_subject.subject--has-room,
.subjects_subject.subject--has-user {
  padding-right: 20px;
}

.subjects_subject.subject--has-room.subject--has-user {
  padding-right: 26px;
}


.subjects_subject.subject--has-room:after {
  content: "\ea38";
  position: absolute;
  top: 9px;
  right: 3px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.subjects_subject.subject--has-user:after {
  content: "\eaf7";
  position: absolute;
  top: 9px;
  right: 3px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.subjects_subject.subject--has-room.subject--has-user:not(.subject--has-other-klass):after {
  content: '';
  width: 18px;
  top: 10px;
  height: 18px;
  background-image: url('https://app.docendo.dk/static/img/app/bulk_create_subjects_userlocation.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.subjects_subject.subject--has-other-klass {
  padding-right: 20px;
}

.subjects_subject.subject--has-other-klass.subject--has-user,
.subjects_subject.subject--has-other-klass.subject--has-room {
  padding-right: 26px;
}

.subjects_subject.subject--has-other-klass.subject--has-room.subject--has-user {
  padding-right: 32px;
}

.subjects_subject.subject--has-other-klass:after {
  content: "\EA62";
  position: absolute;
  top: 9px;
  right: 3px;
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  color: #ffffff;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.subjects_subject.subject--has-other-klass.subject--has-user:not(.subject--has-room):after {
  content: '';
  width: 22px;
  top: 9px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_userklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.subjects_subject.subject--has-other-klass.subject--has-room:not(.subject--has-user):after {
  content: '';
  width: 18px;
  top: 9px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_locationklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.subjects_subject.subject--has-other-klass.subject--has-room.subject--has-user:after {
  content: '';
  width: 29px;
  top: 9px;
  height: 18px;
  background-image: url('../../assets/img/bulk_create_subjects_userlocationklass.png');
  background-size: 100%;
  background-repeat: no-repeat;
}

.d-box {
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #fff;
  padding: 8px;
  overflow: hidden;
  border-radius: 4px;
}

.d-box.users-list {
  padding: 0;
  background: none;
  box-shadow: none;
}

.user-sorter {
  cursor: pointer;
  transition: transform 0.25s;
  cursor: pointer;
  user-select: none;
  opacity: 0.8;
}

.user-sorter > span {
  opacity: 0.3;
}

.user-sorter:active {
  transform: scale(0.95);
}

.user-sorter.user-sorter--active {
  color: var(--blue);
  opacity: 1;
}

.user-sorter.user-sorter--active > span {
  opacity: 1;
}


.user-item__header.user-item {
  font-weight: bold;
  cursor: default !important;
  background: none;
  box-shadow: none;
  margin-bottom: 0;
  padding: 0 0 4px 0;
  background: transparent;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 6px;
  margin-bottom: 4px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background-color: #ffffff;
  border-radius: 4px;
}

.user-item .icon-arrow-down4 {
  font-size: 0.8rem;
  margin-left: 0.5rem;
}

.user-item.user-item--is-open {
  background: #fff !important;
  cursor: default;
}

ul.users-list > li.user-item:hover:not(.user-item__header) {
  background-color: #f6f9fc;
}

.user-item__name {
  display: flex;
  align-items: center;
}

.user-item__name__img {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin: 0 5px;
}

.user-item__name__bttn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
}

.user-item__name__bttn .tw-icon {
  width: 1.4rem;
}

.user-item__name__bttn .secondary {
  fill: #0D2B3E;
}

.user-item__name__bttn .primary {
  fill: #a5b3bb;
}

.user-item__name span {
  font-size: 10px;
}

.user-task__container {
  height: calc(100vh - 2rem);
  width: calc(100vw - 2rem);
  max-width: 1440px;
  padding: 2rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.user-task__container__header {
  margin: -1rem -2rem 1rem -2rem;
  border-radius: 4px 4px 0 0;
  display: flex;
  padding: 0 2rem;
  height: 2rem;
  justify-content: space-between;
  align-items: center;
}

/* EXTRA STYLING */
.flipper-buttons {
  display: flex;
  justify-content: flex-end;
}

.flipper-buttons__button {
  display: flex;
  align-items: center;
  background-color: transparent;
  color: rgba(44, 62, 80, 0.65);
  height: 38px;
  min-width: 130px;
  margin: 0;
  border-radius: 0;
}

.flipper-buttons__button:first-child {
  border-radius: 3px 0 0 3px;
}

.flipper-buttons__button:nth-child(2) {
  border-radius: 0 3px 3px 0;
}

.flipper-buttons__button.active {
  background-color: white;
  color: #2c3e50;
  border: none;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2);
}

.flipper-buttons__button.active > span {
  color: var(--blue);
}

.flipper-buttons__button span {
  margin-right: 10px;
}

.modal-enter-active, .modal-leave-active {
  transition: all 0.25s;
}

.modal-enter, .modal-leave-to {
  transform: scale(0.75);
  opacity: 0;
}

.explainer-text {
  color: grey;
  font-style: italic;
}

.subject-dist__header__right > label {
  padding-right: 5px;
  user-select: none;
}

.print-label {
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
}

.doTable__klass__header__flex {
  display: flex;
  align-items: center;
}

.label-with-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border-radius: 4px;
  margin: 0 -0.5rem;
  cursor: pointer;
  transition: 0.15s ease;
  gap: 0.5rem;
}

.label-with-toggle label {
  cursor: pointer;
}

.label-with-toggle:hover {
  background: aliceblue;
}

.marg-05-right {
  margin-right: 0.5rem;
}

.d-modal {
  width: 500px;
}

.d-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.d-modal__content label {
  max-width: 400px;
}

.do-bttn--menu-button {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
  gap: 0.75rem;
  box-shadow: 0 0 rgba(0, 0, 0, 0), 0 0 rgba(0, 0, 0, 0), 0 1px 2px 0 rgba(0, 0, 0, .05);
}

.do-bttn--menu-button__list > li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.do-bttn--menu-button__list__print > li {
  gap: 0.5rem;
  justify-content: flex-start;
}

.zen-auto-schedule-menu__link {
  display: block;
  margin: -0.5rem !important;
  padding: .5rem;
  color: inherit;
  text-decoration: none;
}

.do-bttn--menu-button.bttn-export {
  background: -webkit-linear-gradient(left, #4553ff, #7f7fd5);
  color: #ffffff;
}

.bulk-dates-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  margin-bottom: 10px;
  background-color: #eef6ff;
  border: 1px solid #bcdcff;
  border-radius: 4px;
}

.bulk-dates-bar__count {
  font-size: 13px;
  color: #334155;
  font-weight: 600;
}

.bulk-dates-bar__cancel {
  margin-left: auto;
}

</style>

<style>
@media print {
  .subject-dist:not(.subject-dist--print-table) .subject-dist__body {
    display: none;
  }

  .subject-dist--print-table .doTable__users,
  .subject-dist--print-table .subject-dist__header {
    display: none !important;
  }

  .subject-dist--print-table .subject-dist__body {
    height: auto;
    display: block;
  }

  .subject-dist--print-table .subjects-and-tasks {
    overflow: visible;
    height: auto;
  }

  .subject-dist--print-table .subject-table {
    break-inside: avoid;
  }

  .subject-dist--print-table .subject-table .do-table tbody tr:hover {
    background-color: transparent;
  }

  .subject-dist--print-table .subject-table__wrench,
  .subject-dist--print-table .bulk-dates-bar {
    display: none !important;
  }

  .d-dropdown {
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// SubjectsDistribution.vue?510ea647